import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import ProductRowsTable from "./components/ProductRowsTable";
import { OrderSerial } from "models/OrderSerial";
import { ProductRow } from "models/Order";
import AddOrderSerialDialog from "dialogs/AddOrderSerialDialog";
import PageLayout from "components/PageLayout";
import Breadcrumbs from "features/breadcrumbs/Breadcrumbs";
import Section from "components/Section";
import OutlinedCard from "components/OutlinedCard";
import { useChecklistContext } from "context/ChecklistContext";
import { useOrderContext } from "context/OrderContext";
import { Product } from "models/Product";
import { orderSerialToProduct } from "utilities/serialEntityToOrderSerial";
import AvailableSerialsTable from "./components/AvailableSerialsTable";

const InstallProductPage: React.FC = () => {
  const order = useOrderContext();
  const { checklist } = useChecklistContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rowType, setRowType] = useState(searchParams.get("rowType") ?? "serienummer");
  const [showAddNewSerial, setShowAddNewSerial] = useState(false);
  const [selectedSerial, setSelectedSerial] = useState<OrderSerial | undefined>();

  useEffect(() => {
    setSearchParams((prev) => ({ ...prev, rowType }), { replace: true });
  }, [rowType, setSearchParams]);

  const availableSerials = useMemo(() => {
    return (
      order.productRows
        .flatMap((row) => row.serials)
        .filter((serial) => !serial.installed && !serial.doa)
        .map((serial) => orderSerialToProduct(serial)) ?? []
    );
  }, [order.productRows]);

  const handleSelectSerial = (product: Product) => {
    setSelectedSerial(product as OrderSerial);
    setShowAddNewSerial(true);
  };

  const handleSelectRow = (row: ProductRow) => {
    navigate(`/orders/view/${order.ordernr}/checklist/${checklist.id}/products/${row.rowId}`);
  };

  return (
    <PageLayout>
      <Breadcrumbs />

      <Section>
        <OutlinedCard>
          <ToggleButtonGroup
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            onChange={(_, [value]) => setRowType(value)}
          >
            <ToggleButton
              value="serienummer"
              selected={rowType === "serienummer"}
              sx={{ fontWeight: "bold" }}
            >
              Serienummer
            </ToggleButton>

            <ToggleButton
              value="artikelrader"
              selected={rowType === "artikelrader"}
              sx={{ fontWeight: "bold" }}
            >
              Artikelrader
            </ToggleButton>
          </ToggleButtonGroup>

          {rowType === "serienummer" && (
            <AvailableSerialsTable serials={availableSerials} onSelect={handleSelectSerial} />
          )}

          {rowType === "artikelrader" && (
            <ProductRowsTable rows={order.productRows} onSelect={handleSelectRow} />
          )}
        </OutlinedCard>
      </Section>

      {showAddNewSerial && (
        <AddOrderSerialDialog serial={selectedSerial!} onClose={() => setShowAddNewSerial(false)} />
      )}
    </PageLayout>
  );
};

export default InstallProductPage;
